/**
 * Home Page Router for trAInedup.ai
 * Routes users to different homepages based on authentication and subscription status.
 * Authenticated + verified users are handled by DashboardRouter which renders one of
 * four personalised dashboard states based on their progress.
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { HomePage } from './HomePage';
import { DashboardRouter } from '../components/dashboard/DashboardRouter';
import { Layout } from '../components/common/Layout';

/**
 * Smart router component that displays different homepages based on user state:
 * - Not logged in / unverified: Standard marketing landing page
 * - Logged in & verified: DashboardRouter (State 1–4 based on user progress)
 */
export const HomePageRouter: React.FC = () => {
  const { user, isAuthenticated, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — show standard landing page
  if (!isAuthenticated || !user) {
    return <HomePage />;
  }

  // Unverified email — show landing page (verification banner is shown via Layout elsewhere)
  if (!user.emailVerified) {
    return <HomePage />;
  }

  // Authenticated + verified — render personalised dashboard inside the shared Layout
  return (
    <Layout>
      <DashboardRouter />
    </Layout>
  );
};

